import type { FileExtensionProps } from '@shared/ui';
import { Collapse, Stack } from '@mui/material';
import { DocumentFileRow, type DocumentFileRowStatusTone } from '@shared/document';
import React from 'react';
import type {
  FileUploadDocumentRowMeta,
  FileUploadFileRowMeta,
  FileUploadLibraryOption,
} from './types';

function fileRowKey(file: File, index: number): string {
  return `${file.name}-${file.size}-${index}`;
}

function documentRowKey(id: number): string {
  return `doc-${id}`;
}

type AnimatedFileRow = {
  key: string;
  file: File;
  meta: FileUploadFileRowMeta;
  index: number;
  visible: boolean;
};

type AnimatedDocumentRow = {
  key: string;
  id: number;
  doc: FileUploadLibraryOption;
  meta: FileUploadDocumentRowMeta;
  visible: boolean;
};

function fileMetaEqual(a: FileUploadFileRowMeta, b: FileUploadFileRowMeta): boolean {
  return (
    a.rowKey === b.rowKey &&
    a.status === b.status &&
    a.progress === b.progress &&
    a.etaLabel === b.etaLabel &&
    a.errorMessage === b.errorMessage &&
    a.rename === b.rename
  );
}

function documentMetaEqual(a: FileUploadDocumentRowMeta, b: FileUploadDocumentRowMeta): boolean {
  return (
    a.rowKey === b.rowKey &&
    a.status === b.status &&
    a.progress === b.progress &&
    a.etaLabel === b.etaLabel &&
    a.errorMessage === b.errorMessage &&
    a.rename === b.rename
  );
}

function animatedFileRowsEqual(prev: AnimatedFileRow[], next: AnimatedFileRow[]): boolean {
  return (
    prev.length === next.length &&
    prev.every((row, index) => {
      const nextRow = next[index];
      return (
        row.key === nextRow.key &&
        row.file === nextRow.file &&
        row.index === nextRow.index &&
        row.visible === nextRow.visible &&
        fileMetaEqual(row.meta, nextRow.meta)
      );
    })
  );
}

function animatedDocumentRowsEqual(
  prev: AnimatedDocumentRow[],
  next: AnimatedDocumentRow[],
): boolean {
  return (
    prev.length === next.length &&
    prev.every((row, index) => {
      const nextRow = next[index];
      return (
        row.key === nextRow.key &&
        row.id === nextRow.id &&
        row.doc === nextRow.doc &&
        row.visible === nextRow.visible &&
        documentMetaEqual(row.meta, nextRow.meta)
      );
    })
  );
}

function statusLabel(
  status: NonNullable<FileUploadFileRowMeta['status']>,
  progress?: number,
  etaLabel?: string,
  errorMessage?: string,
): {
  primary: string;
  tone: DocumentFileRowStatusTone;
  trailing?: string;
} {
  switch (status) {
    case 'uploading': {
      return {
        primary: etaLabel || 'Uploading',
        tone: 'muted',
        trailing: progress != null ? `${Math.round(progress)}%` : undefined,
      };
    }
    case 'completed':
      return { primary: 'Completed', tone: 'success' };
    case 'error':
      return { primary: errorMessage || 'Error', tone: 'error' };
    default:
      return { primary: '', tone: 'muted' };
  }
}

export interface FileUploadAttachmentListProps {
  files?: File[];
  /** Parallel to `files` (same index). */
  filesMeta?: FileUploadFileRowMeta[];
  fileExtensionSize?: FileExtensionProps['size'];
  documents?: FileUploadLibraryOption[];
  metaByDocumentId?: Record<number, FileUploadDocumentRowMeta>;
  disabled?: boolean;
  onRemoveFile?: (index: number) => void;
  onRemoveDocument?: (documentId: number) => void;
  /**
   * Where a *document* row links to (e.g. the document view page); the row opens it in
   * a new tab. Return `undefined` for a row that has nothing to open yet.
   */
  getDocumentHref?: (documentId: number) => string | undefined;
  /**
   * Enable inline rename (hover pencil) for saved document rows, through the audit-document
   * rename. A row whose meta carries `rename` renames through that instead.
   */
  enableDocumentRename?: boolean;
  /**
   * Called with a row's new name once the server has taken its inline rename. A caller whose
   * `documents` are its own snapshot (not a query the rename refreshes) needs it to keep
   * the row's `name` current — the rename field opens on, and compares against, `name`.
   */
  onDocumentRenamed?: (documentId: number, name: string) => void;
  /** Trailing actions (e.g. a kebab menu) rendered on each *document* row. */
  renderDocumentActions?: (doc: FileUploadLibraryOption) => React.ReactNode;
  /** A tag drawn after each *document* row's name (`DocumentFileRow.titleAdornment`). */
  renderDocumentTitleAdornment?: (doc: FileUploadLibraryOption) => React.ReactNode;
  /** Show the "Uploaded by" line on document rows. Default `true`. */
  showUploader?: boolean;
}

/**
 * Card-style rows for selected files and library documents, with optional progress / error UI.
 */
export function FileUploadAttachmentList({
  files = [],
  filesMeta = [],
  documents = [],
  metaByDocumentId = {},
  disabled = false,
  onRemoveFile,
  onRemoveDocument,
  getDocumentHref,
  enableDocumentRename = false,
  onDocumentRenamed,
  renderDocumentActions,
  renderDocumentTitleAdornment,
  fileExtensionSize = 'middle',
  showUploader = true,
}: FileUploadAttachmentListProps) {
  const [animatedFiles, setAnimatedFiles] = React.useState<AnimatedFileRow[]>([]);
  const [animatedDocuments, setAnimatedDocuments] = React.useState<AnimatedDocumentRow[]>([]);

  React.useEffect(() => {
    setAnimatedFiles((prev) => {
      const nextRows = files.map((file, index) => {
        const meta = filesMeta[index] ?? {};
        const key = meta.rowKey ?? fileRowKey(file, index);
        const previousRow = prev.find((row) => row.key === key);

        return {
          ...(previousRow ?? {}),
          key,
          file,
          meta,
          index,
          visible: true,
        };
      });

      prev.forEach((row) => {
        if (!nextRows.some((nextRow) => nextRow.key === row.key)) {
          nextRows.push({ ...row, visible: false });
        }
      });

      return animatedFileRowsEqual(prev, nextRows) ? prev : nextRows;
    });
  }, [files, filesMeta]);

  React.useEffect(() => {
    setAnimatedDocuments((prev) => {
      const nextRows = documents.map((doc) => {
        const meta = metaByDocumentId[doc.id] ?? {};
        // `rowKey` keeps one row in place while its id changes (an upload's temp id
        // handing over to the saved document), instead of collapsing one and adding another.
        const key = meta.rowKey ?? documentRowKey(doc.id);
        const previousRow = prev.find((row) => row.key === key);

        return {
          ...(previousRow ?? {}),
          key,
          id: doc.id,
          doc,
          meta,
          visible: true,
        };
      });

      prev.forEach((row) => {
        if (!nextRows.some((nextRow) => nextRow.key === row.key)) {
          nextRows.push({ ...row, visible: false });
        }
      });

      return animatedDocumentRowsEqual(prev, nextRows) ? prev : nextRows;
    });
  }, [documents, metaByDocumentId]);

  if (animatedFiles.length === 0 && animatedDocuments.length === 0) return null;

  return (
    <Stack spacing={1} data-testid='file-upload-attachment-list'>
      {animatedDocuments.map(({ key, doc, meta, visible }) => {
        const status = meta.status ?? 'idle';
        const sl =
          status !== 'idle'
            ? statusLabel(status, meta.progress, meta.etaLabel, meta.errorMessage)
            : null;
        return (
          <Collapse
            key={key}
            in={visible}
            timeout={180}
            unmountOnExit
            onExited={() => {
              setAnimatedDocuments((prev) => prev.filter((row) => row.key !== key));
            }}
          >
            <DocumentFileRow
              data-testid={`file-upload-attachment-doc-${doc.id}`}
              name={meta.rename?.name ?? doc.name}
              documentUrl={doc.document}
              onRenameCommit={meta.rename?.onCommit}
              renameMaxLength={meta.rename?.maxLength}
              editableName={enableDocumentRename}
              onRenameSaved={
                onDocumentRenamed ? (name) => onDocumentRenamed(doc.id, name) : undefined
              }
              documentId={doc.id}
              iconSize={fileExtensionSize}
              sizeBytes={doc.document_size}
              backlinks={doc.backlinks}
              addedBy={showUploader ? doc.addedBy : undefined}
              statusLabel={sl?.primary}
              statusTone={sl?.tone}
              trailingLabel={sl?.trailing}
              progress={status === 'uploading' ? meta.progress : undefined}
              disabled={disabled}
              href={getDocumentHref?.(doc.id)}
              onRemove={onRemoveDocument ? () => onRemoveDocument(doc.id) : undefined}
              removeLabel='Remove document'
              actionDataTestId={`file-upload-remove-doc-${doc.id}`}
              moreActions={renderDocumentActions?.(doc)}
              titleAdornment={renderDocumentTitleAdornment?.(doc)}
            />
          </Collapse>
        );
      })}
      {animatedFiles.map(({ key, file, meta, index, visible }) => {
        const status = meta.status ?? 'idle';
        const sl =
          status !== 'idle'
            ? statusLabel(status, meta.progress, meta.etaLabel, meta.errorMessage)
            : null;
        return (
          <Collapse
            key={key}
            in={visible}
            timeout={180}
            unmountOnExit
            onExited={() => {
              setAnimatedFiles((prev) => prev.filter((row) => row.key !== key));
            }}
          >
            <DocumentFileRow
              data-testid={`file-upload-attachment-file-${index}`}
              name={meta.rename?.name ?? file.name}
              documentUrl={file.name}
              onRenameCommit={meta.rename?.onCommit}
              renameMaxLength={meta.rename?.maxLength}
              disableLink
              iconSize={fileExtensionSize}
              sizeBytes={file.size}
              statusLabel={sl?.primary}
              statusTone={sl?.tone}
              trailingLabel={sl?.trailing}
              progress={status === 'uploading' ? meta.progress : undefined}
              disabled={disabled}
              onRemove={onRemoveFile ? () => onRemoveFile(index) : undefined}
              removeLabel='Remove file'
              actionDataTestId={`file-upload-remove-file-${index}`}
            />
          </Collapse>
        );
      })}
    </Stack>
  );
}
